import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Button, ConfigProvider, Space, Tooltip } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { RedoOutlined, UndoOutlined } from '@ant-design/icons'
import type { JSONPatchOperation } from 'immutable-json-patch'
import { editStructured, structuredView } from '../config/structured'
import { additionsAt, pointerOf, type FieldEdit, type FieldKind, type FieldPath,
  type StructuredField } from '../config/groups'
import { GroupedTable } from './GroupedTable'
import { FieldsEditor } from './FieldsEditor'
import { fieldRows } from '../config/fields'

type Props = { content: string; onChange: (content: string) => void; disabled: boolean; path?: string;
  onAdvanced?: () => void; toolbarContainer?: HTMLElement | null }
type History = { past: string[]; future: string[] }
const ROOT_PATH: FieldPath = []
const EMPTY_HISTORY: History = { past: [], future: [] }
const UNDO_ICON = <UndoOutlined aria-hidden />
const REDO_ICON = <RedoOutlined aria-hidden />
/** The shared draft owns text; this component owns only reversible local edit history. */
export const StructuredEditor = ({ content, onChange, disabled, path = '', onAdvanced, toolbarContainer }: Props) => {
  const view = useMemo(() => structuredView(content), [content])
  const root = view.kind === 'editable' ? view.value : null
  const [history, setHistory] = useState<History>(EMPTY_HISTORY)
  const [failure, setFailure] = useState<string | null>(null)
  const live = useRef(true)
  const source = useRef(content)
  const file = useRef(path)
  const scope = useMemo(() => ({ content, path, disabled }), [content, path, disabled])
  const latestScope = useRef(scope)
  const latest = useRef({ content, path, disabled, onChange })
  useLayoutEffect(() => { live.current = true; return () => { live.current = false } }, [])
  useLayoutEffect(() => {
    latest.current = { content, path, disabled, onChange }; latestScope.current = scope
    if (content !== source.current || path !== file.current) {
      source.current = content; file.current = path; setHistory(EMPTY_HISTORY); setFailure(null)
    }
  }, [content, disabled, onChange, path, scope])
  const writable = useCallback(() => live.current && !latest.current.disabled && latestScope.current === scope
    && source.current === scope.content, [scope])
  const apply = useCallback((operations: JSONPatchOperation[]) => {
    if (!writable()) return
    try {
      const next = editStructured(content, operations)
      if (next === content) return
      latest.current.onChange(next)
      source.current = next; setHistory((previous) => ({ past: [...previous.past, content], future: [] })); setFailure(null)
    } catch { setFailure('未应用此次编辑，请检查内容后重试') }
  }, [content, writable])
  const edit = useCallback(({ field, value: next }: FieldEdit) => apply([
    { op: 'test', path: pointerOf(field.path), value: field.value },
    { op: 'replace', path: pointerOf(field.path), value: next },
  ]), [apply])
  const remove = useCallback((field: StructuredField) => {
    apply([{ op: 'test', path: pointerOf(field.path), value: field.value }, { op: 'remove', path: pointerOf(field.path) }])
  }, [apply])
  const add = useCallback((at: FieldPath, kind: FieldKind, name: string) => {
    if (!writable()) return
    try { apply(additionsAt(root, at, kind, name)) }
    catch (error) { setFailure((error as Error).message); throw error }
  }, [apply, root, writable])
  const travel = useCallback((direction: 'past' | 'future') => {
    if (!writable()) return
    const target = history[direction].slice(-1)[0]
    if (target === undefined) return
    const changes = {
      past: { past: history.past.slice(0, -1), future: [...history.future, content] },
      future: { past: [...history.past, content], future: history.future.slice(0, -1) },
    }
    try {
      latest.current.onChange(target)
      source.current = target; setHistory(changes[direction]); setFailure(null)
    } catch { setFailure('未应用此次编辑，请检查内容后重试') }
  }, [content, history, writable])
  const undo = useCallback(() => travel('past'), [travel])
  const redo = useCallback(() => travel('future'), [travel])
  if (view.kind !== 'editable') return <Alert type={view.kind === 'invalid' ? 'error' : 'warning'}
    title={view.kind === 'invalid' ? '配置格式有误' : '此内容需使用源码编辑'}
    description="原内容已保留，请通过高级入口检查源码后重试。" showIcon role="alert" />
  return <ConfigProvider locale={zhCN}><section className="instance-administration__structured" aria-label="结构编辑">
    {failure ? <Alert type="error" title="未应用此次编辑" description={failure} showIcon role="alert" /> : null}
    {/* History stays with this editor; only its controls render in the parent's shared toolbar. */}
    {toolbarContainer ? createPortal(<Space>
      <Tooltip title="撤销"><span><Button icon={UNDO_ICON} aria-label="撤销" onClick={undo}
        disabled={disabled || !history.past.length} /></span></Tooltip>
      <Tooltip title="重做"><span><Button icon={REDO_ICON} aria-label="重做" onClick={redo}
        disabled={disabled || !history.future.length} /></span></Tooltip>
    </Space>, toolbarContainer) : null}
    {path === 'fields.yaml' && fieldRows(root) !== null
      ? <FieldsEditor value={root} disabled={disabled} onApply={apply} onAdvanced={onAdvanced} />
      : <GroupedTable key={path} value={root} path={ROOT_PATH} disabled={disabled}
        onEdit={edit} onRemove={remove} onAdd={add} onAdvanced={onAdvanced} />}

  </section></ConfigProvider>
}
